import React, { ReactNode, useState } from 'react';
import { Modal, InputText, ChipToggle } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import DeleteModal from 'components/shared/DeleteModal';
import UseTemplateModalBody from 'components/projects/UseTemplateModalBody';
import { PageTemplateId } from 'data/v3/templates/pages/types';
import { Filter } from 'data/v3';
import styled from 'styled-components';

const modalContainerStyle = {
  minHeight: '80%',
};

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--cc_size_spacing_s);
  margin: var(--cc_size_spacing_l) 0;
`;

export interface TemplateAndExampleObject {
  id: number | string;
  title: string;
  description?: string;
  lastUsedAt?: string;
  isExample?: boolean;
}

type UseTemplateModalProps<T> = {
  title?: string;
  recentTemplates: T[];
  templates: T[];
  isLoading: boolean;
  onSearchChanged: (searchTerm: string) => void;
  searchTerm: string;
  previewContent: ReactNode;
  onCreateFromTemplate: (templateId: PageTemplateId) => void;
  onPreview: (id: PageTemplateId) => void;
  onEdit: (id: PageTemplateId) => void;
  onDelete: (deletedId: PageTemplateId) => void;
  isError: boolean;
  emptyState: ReactNode;
  filters?: Filter[];
  activeFilter?: Filter;
  setActiveFilter?: (filter: Filter) => void;
  onCloseModal: () => void;
};

const UseTemplateModal = <T extends TemplateAndExampleObject>({
  title,
  recentTemplates,
  templates,
  isLoading,
  onSearchChanged,
  searchTerm,
  previewContent,
  onCreateFromTemplate,
  onPreview,
  onEdit,
  onDelete,
  isError,
  emptyState,
  filters,
  activeFilter,
  setActiveFilter,
  onCloseModal,
}: UseTemplateModalProps<T>) => {
  const [deleteModalId, setDeleteModalId] = useState(null);

  const handleDeleteClick = (deleteTemplateId: number) => {
    setDeleteModalId(deleteTemplateId);
  };

  const closeDeleteModal = () => {
    setDeleteModalId(null);
  };

  const handleTemplateTitleClick = (templateId: number) => {
    onCreateFromTemplate(templateId);
    onCloseModal();
  };

  if (previewContent) {
    return (
      <Modal
        isOpen
        onClose={onCloseModal}
        title={title || translate('useTemplateModal.defaultTitle')}
        size="medium"
        headerComponent={null}
        bodyStyle={{
          paddingTop: 0,
        }}
        containerStyle={modalContainerStyle}
      >
        {previewContent}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onCloseModal}
      title={title || translate('useTemplateModal.defaultTitle')}
      size="medium"
      containerStyle={modalContainerStyle}
      data-testid="project__pages-use-template-modal"
    >
      {onSearchChanged && !isLoading ? (
        <InputText
          id="page-template-search-input"
          label=""
          placeholder={translate('useTemplateModal.searchPlaceholder')}
          onChange={(e) => onSearchChanged(e.target.value)}
          status="normal"
          value={searchTerm}
          type="search"
          disabled={
            (searchTerm.length === 0 && templates.length === 0) || isError
          }
        />
      ) : null}

      <ChipRow>
        {filters?.map((filter) => (
          <ChipToggle
            key={filter.key}
            pressed={filter.key === activeFilter.key}
            onClick={() => setActiveFilter(filter)}
          >
            {filter.label}
          </ChipToggle>
        ))}
      </ChipRow>

      <UseTemplateModalBody<T>
        isLoading={isLoading}
        recentTemplates={recentTemplates}
        templates={templates}
        onUseTemplate={handleTemplateTitleClick}
        onPreview={onPreview}
        onEdit={onEdit}
        onDelete={handleDeleteClick}
        emptyState={emptyState}
        isError={isError}
      />

      {deleteModalId ? (
        <DeleteModal
          isOpen
          onClose={closeDeleteModal}
          onDelete={() => onDelete(deleteModalId)}
          title={translate('useTemplateModal.deleteModal.title')}
          message={translate('useTemplateModal.deleteModal.body')}
        />
      ) : null}
    </Modal>
  );
};

export default UseTemplateModal;
